import './App.css';
import './DarkMode.css';

import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { isTokenExpired } from './utils/jwt';
import { initAmplitude, trackEvent, syncSessionReplayForPath } from './utils/amplitude';
import { WorkspaceProvider } from './WorkspaceContext';
import { WorkspaceTabProvider } from './WorkspaceTabContext';
import { DarkModeProvider } from './DarkModeContext';
import { CreditProvider } from './CreditContext';
import { VideoGenerationProvider } from './VideoGenerationContext';
import { NewsProvider } from './NewsContext';

// Eagerly load most frequently accessed components
import LandingPage from './LandingPage';
import LandingPage1 from './LandingPage1';
import LandingPage2 from './LandingPage2';
import LandingPage3 from './LandingPage3';
import Workspace from './Workspace';
import PostersCoversPage from './PostersCoversPage';
import Workspace2 from './Workspace2';
import SidebarLayout from './SidebarLayout';
import OnboardingPage from './OnboardingPage';
import WorkspaceJoin from './WorkspaceJoin';
import ConfidentialityRules from './ConfidentialityRules';
import PrivacyPolicy from './PrivacyPolicy';
import TermsOfService from './TermsOfService';
import AccountDeletion from './AccountDeletion';
import MerraComparison from './MerraComparison';
import About from './About';
import Founder from './Founder';
import Career from './Career';
import MerraOrManual from './MerraOrManual';
import BlogIndex from './BlogIndex';
import BlogPost from './BlogPost';
import Pricing from './Pricing';
import Contact from './Contact';
import NegotiationBrainManifesto from './NegotiationBrainManifesto';
import AmbassadorPrograms from './AmbassadorPrograms';
import NotFound from './NotFound';
import Sitemap from './Sitemap';
import Status from './Status';
import GetLateCallback from './GetLateCallback';
import AiVideoMakerLanding from './seo-landings/AiVideoMakerLanding';
import AiVlogMakerLanding from './seo-landings/AiVlogMakerLanding';
import AiMinivlogMakerLanding from './seo-landings/AiMinivlogMakerLanding';
import AiDayInTheLifeMakerLanding from './seo-landings/AiDayInTheLifeMakerLanding';
import AiGetReadyWithMeMakerLanding from './seo-landings/AiGetReadyWithMeMakerLanding';
import UseCasesPage from './seo-landings/UseCasesPage';
import TikTokShadowbanChecker from './seo-landings/TikTokShadowbanChecker';
import InstagramShadowbanChecker from './seo-landings/InstagramShadowbanChecker';
import WhenToPostTikTok from './seo-landings/WhenToPostTikTok';
import WhenToPostInstagram from './seo-landings/WhenToPostInstagram';

// Lazy load less frequently accessed components
const Dashboard = lazy(() => import('./Dashboard'));
const FileUpload = lazy(() => import('./FileUpload'));
const FilesPage = lazy(() => import('./FilesPage'));
const FileUploadDark = lazy(() => import('./FileUploadDark'));
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
const BrollIdeas = lazy(() => import('./BrollIdeas'));
const Password = lazy(() => import('./password'));
const Counter = lazy(() => import('./CounterPage'));
const Home = lazy(() => import('./Home'));
const ContentCalendar = lazy(() => import('./ContentCalendar'));
const Settings = lazy(() => import('./Settings'));
const News = lazy(() => import('./News'));
const BackOffice = lazy(() => import('./BackOffice'));
const WorkspaceOld = lazy(() => import('./WorkspaceOld'));
const InviteOnboardingPage = lazy(() => import('./InviteOnboardingPage'));
const TheButtonPage = lazy(() => import('./TheButtonPage'));
const RipplePlayground = lazy(() => import('./RipplePlayground'));
const Montage = lazy(() => import('./Montage'));
const Montage2 = lazy(() => import('./Montage2'));
const MediaKit = lazy(() => import('./Media-kit'));
const WorkspaceCalendar = lazy(() => import('./WorkspaceCalendar'));
const ThumbnailsMaker = lazy(() => import('./ThumbnailsMaker'));
const ChromaticPage = lazy(() => import('./ChromaticPage'));

// Loading component for lazy routes
const LoadingFallback = () => (
    <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '18px',
        color: '#666'
    }}>
        Loading...
    </div>
);
// Initialize Amplitude when your app starts
initAmplitude();

function ScrollToTop() {
    const { pathname } = useLocation();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [pathname]);

    // Enable Session Replay when navigating into the webapp (e.g. landing → /workspace).
    useEffect(() => {
        syncSessionReplayForPath(pathname);
    }, [pathname]);

    return null;
}

function App() {
    return (
        <HelmetProvider>
            <DarkModeProvider>
                <CreditProvider>
                    <VideoGenerationProvider>
                        <NewsProvider>
                            <Router>
                                <ScrollToTop />
                                <WorkspaceProvider>
                                    <Routes>
                                        <Route path="/" element={<RootRedirect />} />
                                        <Route path="/workspace" element={
                                            <WorkspaceTabProvider>
                                                <SidebarLayout>
                                                    <Workspace />
                                                </SidebarLayout>
                                            </WorkspaceTabProvider>
                                        } />
                                        <Route path="/workspace/covers" element={
                                            <RequireAuth>
                                                <PostersCoversPage />
                                            </RequireAuth>
                                        } />
                                        <Route path="/workspace/join/:token" element={<WorkspaceJoin />} />
                                        <Route path="/onboarding" element={<OnboardingPage />} />
                                        <Route path="/invite-onboarding/:token" element={
                                            <Suspense fallback={<LoadingFallback />}>
                                                <InviteOnboardingPage />
                                            </Suspense>
                                        } />
                                        <Route path="/workspace2" element={
                                            <SidebarLayout>
                                                <Workspace2 />
                                            </SidebarLayout>
                                        } />
                                        <Route path="/workspace-old" element={
                                            <Suspense fallback={<LoadingFallback />}>
                                                <SidebarLayout>
                                                    <WorkspaceOld />
                                                </SidebarLayout>
                                            </Suspense>
                                        } />
                                        <Route path="/content-calendar" element={
                                            <Suspense fallback={<LoadingFallback />}>
                                                <SidebarLayout>
                                                    <ContentCalendar />
                                                </SidebarLayout>
                                            </Suspense>
                                        } />
                                        <Route path="/settings" element={
                                            <Suspense fallback={<LoadingFallback />}>
                                                <SidebarLayout>
                                                    <Settings />
                                                </SidebarLayout>
                                            </Suspense>
                                        } />
                                        <Route path="/calendar/:workspaceId" element={
                                            <Suspense fallback={<LoadingFallback />}>
                                                <WorkspaceCalendar />
                                            </Suspense>
                                        } />
                                        <Route path="/auth" element={<TokenCheck />} />
                                        <Route path="/getlate/callback" element={<GetLateCallback />} />
                                        <Route path="/landing-page" element={<LandingPage1 />} />
                                        <Route path="/ai-video-maker" element={<AiVideoMakerLanding />} />
                                        <Route path="/ai-vlog-maker" element={<AiVlogMakerLanding />} />
                                        <Route path="/ai-minivlog-maker" element={<AiMinivlogMakerLanding />} />
                                        <Route path="/ai-day-in-the-life-maker" element={<AiDayInTheLifeMakerLanding />} />
                                        <Route path="/ai-get-ready-with-me-maker" element={<AiGetReadyWithMeMakerLanding />} />
                                        <Route path="/tiktok-shadowban-checker" element={<TikTokShadowbanChecker />} />
                                        <Route path="/instagram-shadowban-checker" element={<InstagramShadowbanChecker />} />
                                        <Route path="/when-to-post-tiktok" element={<WhenToPostTikTok />} />
                                        <Route path="/when-to-post-instagram" element={<WhenToPostInstagram />} />
                                        <Route path="/use-cases" element={<UseCasesPage />} />
                                        <Route
                                            path="/dashboard"
                                            element={
                                                <RequireAuth>
                                                    <Suspense fallback={<LoadingFallback />}>
                                                        <Dashboard />
                                                    </Suspense>
                                                </RequireAuth>
                                            }
                                        />
                                        <Route
                                            path="/upload"
                                            element={
                                                <RequireAuth>
                                                    <Suspense fallback={<LoadingFallback />}>
                                                        <FileUpload />
                                                    </Suspense>
                                                </RequireAuth>
                                            }
                                        />
                                        <Route
                                            path="/files"
                                            element={
                                                <RequireAuth>
                                                    <Suspense fallback={<LoadingFallback />}>
                                                        <FilesPage />
                                                    </Suspense>
                                                </RequireAuth>
                                            }
                                        />
                                        <Route
                                            path='/counter'
                                            element={
                                                <RequireAuth>
                                                    <Suspense fallback={<LoadingFallback />}>
                                                        <Counter />
                                                    </Suspense>
                                                </RequireAuth>
                                            }
                                        />
                                        <Route
                                            path="/password"
                                            element={
                                                <RequireAuth>
                                                    <Suspense fallback={<LoadingFallback />}>
                                                        <Password />
                                                    </Suspense>
                                                </RequireAuth>
                                            }
                                        />
                                        <Route
                                            path="/admin"
                                            element={
                                                <RequireAuth>
                                                    <Suspense fallback={<LoadingFallback />}>
                                                        <AdminDashboard />
                                                    </Suspense>
                                                </RequireAuth>
                                            }
                                        />
                                        <Route
                                            path="/backoffice"
                                            element={
                                                <RequireAuth>
                                                    <Suspense fallback={<LoadingFallback />}>
                                                        <BackOffice />
                                                    </Suspense>
                                                </RequireAuth>
                                            }
                                        />
                                        <Route
                                            path="/dark"
                                            element={
                                                <RequireAuth>
                                                    <Suspense fallback={<LoadingFallback />}>
                                                        <FileUploadDark />
                                                    </Suspense>
                                                </RequireAuth>
                                            }
                                        />
                                        <Route path="/confidentiality-rules" element={<ConfidentialityRules />} />
                                        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                                        <Route path="/terms-of-service" element={<TermsOfService />} />
                                        <Route path="/account-deletion" element={<AccountDeletion />} />
                                        <Route path="/merra-comparison" element={<MerraComparison />} />
                                        <Route path="/about" element={<About />} />
                                        <Route path="/founder" element={<Founder />} />
                                        <Route path="/career" element={<Career />} />
                                        <Route path="/merra-or-manual" element={<MerraOrManual />} />
                                        <Route path="/blog" element={<BlogIndex />} />
                                        <Route path="/blog/:slug" element={<BlogPost />} />
                                        <Route path="/negotiation-brain-manifesto" element={<NegotiationBrainManifesto />} />
                                        <Route path="/pricing" element={<Pricing />} />
                                        <Route path="/contact" element={<Contact />} />
                                        <Route path="/ambassador-programs" element={<AmbassadorPrograms />} />
                                        <Route path="/status" element={<Status />} />
                                        <Route path="/landing-page-1" element={<LandingPage />} />
                                        <Route path="/landing-page-2" element={<LandingPage2 />} />
                                        <Route path="/landing-page-3" element={<LandingPage3 />} />
                                        <Route path="/b-roll-ideas" element={
                                            <Suspense fallback={<LoadingFallback />}>
                                                <BrollIdeas />
                                            </Suspense>
                                        } />
                                        <Route path="/news" element={
                                            <Suspense fallback={<LoadingFallback />}>
                                                <News />
                                            </Suspense>
                                        } />
                                        <Route path="/the-button-page" element={
                                            <Suspense fallback={<LoadingFallback />}>
                                                <TheButtonPage />
                                            </Suspense>
                                        } />
                                        <Route path="/ripple" element={
                                            <Suspense fallback={<LoadingFallback />}>
                                                <RipplePlayground />
                                            </Suspense>
                                        } />
                                        <Route path="/sitemap" element={<Sitemap />} />
                                        <Route path="/montage" element={
                                            <Suspense fallback={<LoadingFallback />}>
                                                <Montage />
                                            </Suspense>
                                        } />
                                        <Route path="/montage2" element={
                                            <Suspense fallback={<LoadingFallback />}>
                                                <Montage2 />
                                            </Suspense>
                                        } />
                                        <Route path="/media-kit" element={
                                            <Suspense fallback={<LoadingFallback />}>
                                                <MediaKit />
                                            </Suspense>
                                        } />
                                        <Route path="/thumbnail-maker" element={
                                            <Suspense fallback={<LoadingFallback />}>
                                                <ThumbnailsMaker />
                                            </Suspense>
                                        } />
                                        <Route path="/chromatic" element={
                                            <Suspense fallback={<LoadingFallback />}>
                                                <ChromaticPage />
                                            </Suspense>
                                        } />
                                        <Route path="*" element={<NotFound />} />
                                    </Routes>
                                </WorkspaceProvider>
                            </Router>
                        </NewsProvider>
                    </VideoGenerationProvider>
                </CreditProvider>
            </DarkModeProvider>
        </HelmetProvider>
    );
}

function RootRedirect() {
    const params = new URLSearchParams(window.location.search);
    const tokenFromUrl = params.get('token');

    console.log('RootRedirect - tokenFromUrl:', tokenFromUrl ? 'present' : 'not present');
    console.log('RootRedirect - current path:', window.location.pathname);

    // If there's a token in the URL, store it and redirect
    if (tokenFromUrl) {
        console.log('RootRedirect - storing token from URL');
        localStorage.setItem('token', tokenFromUrl);
        // Clear the token from URL to prevent redirect loops
        window.history.replaceState({}, document.title, window.location.pathname);
        trackEvent('auth_redirect_success', { source: 'url_token' });
        // If user came from invite onboarding (signed in at end), join workspace and go to /workspace
        const pendingInviteToken = localStorage.getItem('pendingInviteToken');
        console.log('RootRedirect - pendingInviteToken:', pendingInviteToken ? 'present' : 'not present');
        if (pendingInviteToken) {
            localStorage.removeItem('pendingInviteToken');
            // Use full redirect to ensure query param is preserved (avoids React Router timing issues)
            window.location.replace(`/workspace/join/${pendingInviteToken}?from=oauth`);
            return null;
        }
        return <Navigate to="/workspace" replace />;
    }

    // Only redirect authenticated users if they're on the root path
    if (window.location.pathname === '/') {
        // Check for existing token in localStorage
        const token = localStorage.getItem('token');
        console.log('RootRedirect - token from localStorage:', token ? 'present' : 'not present');

        if (token && !isTokenExpired(token)) {
            console.log('RootRedirect - valid token found, redirecting to workspace');
            return <Navigate to="/workspace" replace />;
        }

        console.log('RootRedirect - no valid token, redirecting to landing page');
        return <Navigate to="/landing-page" replace />;
    }

    // If not on root path, don't redirect - let other routes handle it
    // console.log('RootRedirect - not on root path, not redirecting');
    return <Navigate to="/landing-page" replace />; //<- if not on root path, redirect to landing page
    // return null; // Don't render anything, let other routes handle it
}

// Component to check token and redirect accordingly
function TokenCheck() {
    const params = new URLSearchParams(window.location.search);
    const tokenFromUrl = params.get('token');

    React.useEffect(() => {
        if (tokenFromUrl) {
            localStorage.setItem('token', tokenFromUrl);
            window.history.replaceState({}, document.title, window.location.pathname);
            window.location.href = '/workspace';
            return;
        }

        const token = localStorage.getItem('token');
        if (token && !isTokenExpired(token)) {
            window.location.href = '/workspace';
        } else {
            window.location.href = '/';
        }
    }, [tokenFromUrl]);

    // Render this while the redirect happens
    return (
        <div>
            <div style={{
                position: 'fixed',
                bottom: '20px',
                left: '0',
                width: '100%',
                textAlign: 'center',
                padding: '10px'
            }}>
                <a href="/confidentiality-rules" style={{ color: '#666', textDecoration: 'none' }}>
                    Privacy Policy
                </a>
            </div>
        </div>
    );
}

// Higher-order component for protected routes
function RequireAuth({ children }) {
    const token = localStorage.getItem('token');

    console.log('RequireAuth - token:', token ? 'present' : 'not present');
    console.log('RequireAuth - isTokenExpired:', token ? isTokenExpired(token) : 'no token');

    // Check for token existence and validity
    if (!token || isTokenExpired(token)) {
        console.log('RequireAuth - redirecting to / due to missing or expired token');
        return <Navigate to="/" />;
    }

    console.log('RequireAuth - token valid, rendering children');
    return children;
}

export default App;
